import { Card, CardContent, CardHeader, Box, Typography, Grid, Alert } from '@mui/material';
import { Title, useLogout } from 'react-admin';
import { useQuery, gql, ApolloError } from '@apollo/client';
import {
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  AreaChart,
  Area,
} from 'recharts';
import PeopleIcon from '@mui/icons-material/People';
import CreditCardIcon from '@mui/icons-material/CreditCard';
import VisibilityIcon from '@mui/icons-material/Visibility';
import ShareIcon from '@mui/icons-material/Share';
import StorefrontIcon from '@mui/icons-material/Storefront';
import EventIcon from '@mui/icons-material/Event';

// GraphQL query pour les statistiques admin
const ADMIN_STATS_QUERY = gql`
  query AdminStats {
    adminStats {
      totalUsers
      totalCards
      totalViews
      totalShares
      presentationCards
      commerceCards
      eventCards
      usersOverTime {
        date
        count
      }
      cardsOverTime {
        date
        count
      }
      viewsOverTime {
        date
        count
      }
    }
  }
`;

/**
 * Stats Card Component
 */
function StatsCard({
  title,
  value,
  icon,
  color = '#C97B63',
  loading = false
}: {
  title: string;
  value: number | string;
  icon: React.ReactNode;
  color?: string;
  loading?: boolean;
}) {
  return (
    <Card sx={{ height: '100%' }}>
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box>
            <Typography variant="body2" color="textSecondary" gutterBottom>
              {title}
            </Typography>
            <Typography variant="h4" fontWeight={600}>
              {loading ? '...' : value}
            </Typography>
          </Box>
          <Box
            sx={{
              width: 56,
              height: 56,
              borderRadius: 2,
              backgroundColor: `${color}20`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: color,
            }}
          >
            {icon}
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
}

/**
 * Formatte une date pour l'affichage dans les graphiques
 */
function formatDate(dateStr: string): string {
  const date = new Date(dateStr);
  return date.toLocaleDateString('fr-FR', { day: '2-digit', month: 'short' });
}

/**
 * Composant graphique d'évolution
 */
function EvolutionChart({
  data,
  dataKey,
  color,
  title,
  loading,
}: {
  data: Array<{ date: string; count: number }>;
  dataKey: string;
  color: string;
  title: string;
  loading: boolean;
}) {
  if (loading) {
    return (
      <Card sx={{ height: 300 }}>
        <CardHeader title={title} />
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: 200 }}>
            <Typography color="textSecondary">Chargement...</Typography>
          </Box>
        </CardContent>
      </Card>
    );
  }

  const formattedData = data.map((item) => ({
    ...item,
    formattedDate: formatDate(item.date),
  }));

  return (
    <Card sx={{ height: 300 }}>
      <CardHeader title={title} titleTypographyProps={{ variant: 'subtitle1', fontWeight: 600 }} />
      <CardContent sx={{ pt: 0 }}>
        <ResponsiveContainer width="100%" height={200}>
          <AreaChart data={formattedData} margin={{ top: 5, right: 20, left: 0, bottom: 5 }}>
            <defs>
              <linearGradient id={`gradient-${dataKey}`} x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor={color} stopOpacity={0.3} />
                <stop offset="95%" stopColor={color} stopOpacity={0} />
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
            <XAxis
              dataKey="formattedDate"
              tick={{ fontSize: 10 }}
              tickLine={false}
              interval="preserveStartEnd"
            />
            <YAxis tick={{ fontSize: 10 }} tickLine={false} axisLine={false} />
            <Tooltip
              contentStyle={{
                backgroundColor: 'white',
                border: '1px solid #e0e0e0',
                borderRadius: 8,
                boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
              }}
              labelFormatter={(label) => label}
              formatter={(value: number) => [value, 'Total']}
            />
            <Area
              type="monotone"
              dataKey="count"
              stroke={color}
              strokeWidth={2}
              fill={`url(#gradient-${dataKey})`}
            />
          </AreaChart>
        </ResponsiveContainer>
      </CardContent>
    </Card>
  );
}

/**
 * Composant pour les cartes par type
 */
function CardsByTypeCard({
  presentationCards,
  commerceCards,
  eventCards,
  loading,
}: {
  presentationCards: number;
  commerceCards: number;
  eventCards: number;
  loading: boolean;
}) {
  const total = presentationCards + commerceCards + eventCards;

  const types = [
    { name: 'Présentation', count: presentationCards, color: '#C97B63', icon: <CreditCardIcon /> },
    { name: 'Commerce', count: commerceCards, color: '#10B981', icon: <StorefrontIcon /> },
    { name: 'Événement', count: eventCards, color: '#F59E0B', icon: <EventIcon /> },
  ];

  return (
    <Card sx={{ height: '100%' }}>
      <CardHeader title="Cartes par type" titleTypographyProps={{ variant: 'subtitle1', fontWeight: 600 }} />
      <CardContent>
        {loading ? (
          <Typography color="textSecondary">Chargement...</Typography>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {types.map((type) => (
              <Box key={type.name} sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Box
                  sx={{
                    width: 40,
                    height: 40,
                    borderRadius: 1,
                    backgroundColor: `${type.color}20`,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: type.color,
                  }}
                >
                  {type.icon}
                </Box>
                <Box sx={{ flex: 1 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                    <Typography variant="body2">{type.name}</Typography>
                    <Typography variant="body2" fontWeight={600}>
                      {type.count}
                    </Typography>
                  </Box>
                  <Box
                    sx={{
                      height: 6,
                      borderRadius: 3,
                      backgroundColor: '#f0f0f0',
                      overflow: 'hidden',
                    }}
                  >
                    <Box
                      sx={{
                        height: '100%',
                        width: `${total > 0 ? (type.count / total) * 100 : 0}%`,
                        backgroundColor: type.color,
                        borderRadius: 3,
                        transition: 'width 0.5s ease',
                      }}
                    />
                  </Box>
                </Box>
              </Box>
            ))}
          </Box>
        )}
      </CardContent>
    </Card>
  );
}

/**
 * EKAA Admin Dashboard
 */
export function Dashboard() {
  const logout = useLogout();

  // Fetch stats from GraphQL
  const { data: statsData, loading: statsLoading, error: statsError } = useQuery(ADMIN_STATS_QUERY, {
    fetchPolicy: 'network-only',
    errorPolicy: 'all',
  });

  const stats = statsData?.adminStats;

  // Check if it's an auth error
  const isAuthError = statsError instanceof ApolloError &&
    statsError.graphQLErrors?.some(
      (e: any) =>
        e.extensions?.code === 'UNAUTHENTICATED' ||
        e.extensions?.code === 'FORBIDDEN' ||
        e.message?.includes('Unauthorized')
    );

  // Handle auth error - redirect to login
  if (isAuthError) {
    logout();
    return null;
  }

  return (
    <Box sx={{ p: 2 }}>
      <Title title="Dashboard" />

      <Typography variant="h4" gutterBottom sx={{ mb: 3 }}>
        Tableau de bord
      </Typography>

      {/* Display error if any */}
      {statsError && !isAuthError && (
        <Alert severity="error" sx={{ mb: 3 }}>
          Erreur de chargement des statistiques: {statsError.message}
        </Alert>
      )}

      {/* Stats Cards */}
      <Grid container spacing={3}>
        <Grid item xs={12} sm={6} md={3}>
          <StatsCard
            title="Utilisateurs"
            value={stats?.totalUsers || 0}
            icon={<PeopleIcon sx={{ fontSize: 28 }} />}
            color="#C97B63"
            loading={statsLoading}
          />
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <StatsCard
            title="Cartes digitales"
            value={stats?.totalCards || 0}
            icon={<CreditCardIcon sx={{ fontSize: 28 }} />}
            color="#D4AF37"
            loading={statsLoading}
          />
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <StatsCard
            title="Vues totales"
            value={stats?.totalViews || 0}
            icon={<VisibilityIcon sx={{ fontSize: 28 }} />}
            color="#10B981"
            loading={statsLoading}
          />
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <StatsCard
            title="Partages totaux"
            value={stats?.totalShares || 0}
            icon={<ShareIcon sx={{ fontSize: 28 }} />}
            color="#6366F1"
            loading={statsLoading}
          />
        </Grid>
      </Grid>

      {/* Evolution Charts */}
      <Typography variant="h6" sx={{ mt: 4, mb: 2 }}>
        Évolution sur 30 jours
      </Typography>

      <Grid container spacing={3}>
        <Grid item xs={12} md={4}>
          <EvolutionChart
            data={stats?.usersOverTime || []}
            dataKey="users"
            color="#C97B63"
            title="Nouveaux utilisateurs"
            loading={statsLoading}
          />
        </Grid>

        <Grid item xs={12} md={4}>
          <EvolutionChart
            data={stats?.cardsOverTime || []}
            dataKey="cards"
            color="#D4AF37"
            title="Nouvelles cartes"
            loading={statsLoading}
          />
        </Grid>

        <Grid item xs={12} md={4}>
          <EvolutionChart
            data={stats?.viewsOverTime || []}
            dataKey="views"
            color="#10B981"
            title="Vues des cartes"
            loading={statsLoading}
          />
        </Grid>
      </Grid>

      {/* Cards by Type */}
      <Grid container spacing={3} sx={{ mt: 2 }}>
        <Grid item xs={12} md={6}>
          <CardsByTypeCard
            presentationCards={stats?.presentationCards || 0}
            commerceCards={stats?.commerceCards || 0}
            eventCards={stats?.eventCards || 0}
            loading={statsLoading}
          />
        </Grid>

        <Grid item xs={12} md={6}>
          <Card sx={{ height: '100%' }}>
            <CardHeader title="Activité récente" titleTypographyProps={{ variant: 'subtitle1', fontWeight: 600 }} />
            <CardContent>
              <Typography variant="body2" color="textSecondary">
                Bienvenue sur le dashboard EKAA Admin. Les statistiques sont mises à jour en temps réel.
              </Typography>
              <Box sx={{ mt: 2 }}>
                <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  <Box sx={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: '#10B981' }} />
                  API connectée
                </Typography>
                <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Box sx={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: '#10B981' }} />
                  GraphQL opérationnel
                </Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
}

export default Dashboard;
